<template>
  <div ref="root" class="sp-hero-item" :class="classModifiers">
    <div class="sp-hero-item__background">
      <div v-if="video" class="sp-hero-item__background-asset">
        <video ref="videoRef" :src="video" :aria-label="title" :autoplay="active" muted loop playsinline />
      </div>

      <div v-else class="sp-hero-item__background-asset">
        <slot name="image">
          <img :src="image" :alt="title" />
        </slot>
      </div>
      <div class="sp-hero-item__background-overlay"></div>
    </div>

    <div class="sp-hero-item__content">
      <component :is="titleTag" v-if="title" class="sp-hero-item__title">
        {{ title }}
      </component>

      <div class="sp-hero-item__content-body">
        <slot />
      </div>

      <sp-hero-action v-if="actionUrl" v-bind="actionProps">{{ actionLabel }}</sp-hero-action>

      <div class="sp-hero-item__captions">
        <slot name="captions">{{ captions }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useExpose } from "../../composables/expose";
import { toBoolean } from "../../utils/props";

const props = defineProps({
  titleTag: {
    type: String,
    default: "h1",
  },
  title: {
    type: String,
    default: undefined,
  },
  image: {
    type: String,
    default: undefined,
  },
  video: {
    type: String,
    default: undefined,
  },
  captions: {
    type: String,
    default: undefined,
  },
  active: {
    type: [Boolean, String],
    default: false,
  },
  align: {
    type: String,
    default: "start",
    validator: (value) => ["start", "center", "end"].includes(value),
  },
  theme: {
    type: String,
    default: "light",
    validator: (value) => ["dark", "light"].includes(value),
  },
  // Action
  actionUrl: {
    type: String,
    default: undefined,
  },
  actionLabel: {
    type: String,
    default: undefined,
  },
  actionColor: {
    type: String,
    default: undefined,
  },
  actionVariant: {
    type: String,
    default: undefined,
  },
});

const root = ref(null);
const active = ref(toBoolean(props.active));
const videoRef = ref(null);
const actionProps = computed(() => ({
  url: props.actionUrl,
  color: props.actionColor,
  variant: props.actionVariant,
  label: props.actionLabel,
}));

const { isSmScreen, isMdScreen } = useBreakpoints(root);
const { exposeMethods } = useExpose(root);

watch(active, (isActive) => {
  if (isActive && videoRef.value) {
    videoRef.value.currentTime = 0;
  }
});

const classModifiers = computed(() => ({
  "--has-title": props.title !== undefined,
  "--has-image": props.image !== undefined,
  "--has-video": props.video !== undefined,
  "--active": active.value,
  "--is-small-screen": isSmScreen.value,
  "--is-medium-screen": isMdScreen.value,
  [`--theme-${props.theme}`]: props.theme,
  [`--align-${props.align}`]: props.align,
}));

exposeMethods({
  setActive(isActive) {
    active.value = toBoolean(isActive);
  },
});
</script>

<style>
:host {
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
}
</style>

<style scoped lang="scss">
.sp-hero-item {
  --font: var(--sp-comp-hero-item-font, var(sp-sys-typescale-body-short));
  --title-font: var(--sp-comp-hero-item-title-font, var(--sp-sys-typescale-h1-short));
  --title-offset: var(--sp-comp-hero-item-title-offset, calc(var(--sp-ref-spacing-8) * -1));

  --content-font: var(--sp-comp-hero-item-content-font, var(--font));
  --content-line-height: var(--sp-comp-hero-item-content-line-height, var(--sp-sys-typescale-body-line-height));
  --content-gap: var(--sp-comp-hero-item-content-gap, 1rem);
  --overlay-dark-background-color: var(
    --sp-comp-hero-item-overlay-dark-start-background-color,
    var(--sp-sys-color-black, #000)
  );
  --overlay-light-background-color: var(
    --sp-comp-hero-item-overlay-light-start-background-color,
    var(--sp-sys-color-white, #fff)
  );

  --padding-inline: var(--sp-comp-hero-item-padding-inline, var(--sp-ref-spacing-32));
  --padding-block: var(--sp-comp-hero-item-padding-block, var(--sp-ref-spacing-32));
  --padding-inline-sm: var(--sp-comp-hero-item-padding-inline-sm, var(--sp-ref-spacing-8));
  --padding-block-sm: var(--sp-comp-hero-item-padding-block-sm, var(--sp-ref-spacing-10) var(--sp-ref-spacing-20));
  --padding-inline-md: var(--sp-comp-hero-item-padding-inline-sm, var(--sp-ref-spacing-8));
  --padding-block-md: var(--sp-comp-hero-item-padding-block-sm, var(--sp-ref-spacing-32) var(--sp-ref-spacing-40));

  --background-width: var(--sp-comp-hero-item-background-width, 100%);
  --border-radius: var(--sp-comp-hero-item-border-radius, 0);
  --background-margin-inline: var(--sp-comp-hero-item-background-margin-inline, 0);
  --content-max-width: var(--sp-comp-hero-item-content-max-width, 40%);
  --content-body-max-width: var(--sp-comp-hero-item-content-body-max-width, 80%);

  display: flex;
  font: var(--font);
  justify-content: v-bind(align);
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity var(--transition-duration, 500ms) ease;

  &.--active {
    opacity: 1;
  }

  &.--align-start {
    --overlay-direction: to right;
  }

  &.--align-center {
    --overlay-direction: to bottom;
  }

  &.--align-end {
    --overlay-direction: to left;
  }

  &.--is-small-screen {
    --overlay-direction: to bottom;
    --content-max-width: 100%;
    --content-body-max-width: 100%;
    --padding-inline: var(--padding-inline-sm);
    --padding-block: var(--padding-block-sm);
    --content-gap: var(--sp-ref-spacing-8);
    --title-font: var(--sp-comp-hero-item-title-font-sm, var(--sp-sys-typescale-h1-small-short));
    --title-offset: var(--sp-comp-hero-item-title-offset-sm, calc(var(--sp-ref-spacing-4) * -1));
    --content-font: var(--sp-comp-hero-item-content-font-sm, var(--sp-sys-typescale-body-small-short));
    --content-line-height: var(
      --sp-comp-hero-item-content-line-height-sm,
      var(--sp-sys-typescale-body-small-line-height)
    );
  }
  &.--is-medium-screen {
    --title-font: var(--sp-comp-hero-item-title-font-md, var(--sp-sys-typescale-h1-short));
    --padding-inline: var(--padding-inline-md);
    --padding-block: var(--padding-block-md);
  }
  &.--theme-dark {
    --overlay-start-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-start-background-color,
      var(--overlay-dark-background-color)
    );
    --overlay-end-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-end-background-color,
      var(--overlay-light-background-color)
    );
    --overlay-start-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );
    --overlay-end-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );
    --color: var(--sp-comp-hero-item-theme-dark-text-color, var(--sp-sys-color-on-black, #fff));
  }

  &.--theme-light {
    --overlay-start-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-start-background-color,
      var(--overlay-light-background-color)
    );
    --overlay-end-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-end-background-color,
      var(--overlay-dark-background-color)
    );
    --overlay-start-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );
    --overlay-end-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );
    --color: var(--sp-comp-hero-item-theme-light-text-color, var(--sp-sys-color-on-white, #000));
  }

  ::slotted(sp-responsive-image) {
    --sp-responsive-image-border-radius: var(--border-radius);
  }

  &__title {
    font: var(--title-font);
    margin-block: 0;
    margin-block-end: var(--title-offset);
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: var(--border-radius);
    width: var(--background-width);
    margin-inline: var(--background-margin-inline);

    &-overlay {
      --overlay-start-gradient: color-mix(
        in srgb,
        var(--overlay-start-background-color) var(--overlay-start-alpha),
        transparent
      );
      --overlay-end-gradient: color-mix(
        in srgb,
        var(--overlay-end-background-color) var(--overlay-end-alpha),
        transparent
      );

      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      border-radius: var(--border-radius);
      background: linear-gradient(var(--overlay-direction), var(--overlay-start-gradient), var(--overlay-end-gradient));
    }

    &-asset {
      width: 100%;
      height: 100%;
    }

    video,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    --sp-comp-layout-row-color-link: var(--color);
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: var(--content-max-width);
    gap: var(--content-gap);
    z-index: 2;
    padding-block: var(--padding-block);
    padding-inline: var(--padding-inline);
    color: var(--color);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

    &-body {
      --sp-comp-wysiwyg-content-font: var(--content-font);
      --sp-comp-wysiwyg-content-line-height: var(--content-line-height);
      display: flex;
      max-width: var(--content-body-max-width);
      flex-direction: column;
      align-self: v-bind(align);
      font: var(--content-font);
    }

    .sp-hero-item__captions {
      flex-grow: 1;
      display: flex;
      align-items: flex-end;
    }
  }
}
</style>
